import type { IntelData } from "./translate";
import type { QueryState } from "../hooks/useQueryWithState";

export interface SqueezePanelSignal {
  symbol: string;
  squeeze_pct: number | null;
  squeeze_tier: string | null;
  momentum_direction: string | null;
  action?: string;
  tf_1h?: TimeframeSqueeze;
  tf_4h?: TimeframeSqueeze;
  tf_daily?: TimeframeSqueeze;
}

export interface TimeframeSqueeze {
  on: boolean | null;
  fired: boolean | null;
  direction: string | null;
  stage: string | null;
  pct: number | null;
  pro_level?: string | null;
  status?: string | null;
  status_reason?: string | null;
}

export interface VolumePanelSignal {
  symbol: string;
  obv_divergence_10: string | null;
  obv_divergence_20: string | null;
  cmf_20: number | null;
  mfi_14: number | null;
  fbd_flow_score: number | null;
  mr_flow_score: number | null;
}

export interface OrderFlowPanelSignal {
  symbol: string;
  iceberg_bid: boolean | null;
  iceberg_ask: boolean | null;
  absorption_bid: boolean | null;
  absorption_ask: boolean | null;
  institutional_direction: string | null;
  institutional_score: number | null;
  spoof_bid_risk: number | null;
  spoof_ask_risk: number | null;
  order_flow_status: string | null;
  order_flow_age_sec: number | null;
  snapshot_created_at?: number | null;
}

export type DataSourceStatus = "loading" | "live" | "error" | "disabled";

export interface CommandCenterDataSourceRow {
  panel: string;
  source: string;
  transport: string;
  freshness: string;
  fallback: string;
  decisionImpact: string;
  behavior: "LIVE" | "SNAPSHOT" | "LOG-ONLY" | "READ-ONLY";
  status: DataSourceStatus;
}

export interface CommandCenterDataQualityRow {
  panel: string;
  fieldGroup: string;
  present: number;
  expected: number;
  missingSymbols: string[];
  severity: "ok" | "watch" | "broken";
}

interface BuildCommandCenterDataSourcesArgs {
  statsState: QueryState<unknown>;
  signalsState: QueryState<unknown>;
  intelState: QueryState<Record<string, unknown>>;
  volumeFlowState: QueryState<Record<string, unknown>>;
  levelForecastState: QueryState<unknown>;
  orderFlowTransportLabel: string;
  admin: boolean;
}

type IntelMap = Record<string, IntelData | undefined>;

type CriticalFieldGroup = {
  panel: string;
  fieldGroup: string;
  fields: (keyof IntelData)[];
  requireAll?: boolean;
};

const CRITICAL_FIELD_GROUPS: CriticalFieldGroup[] = [
  {
    panel: "Instrument cards",
    fieldGroup: "live price + regime",
    fields: ["current_price", "hmm_regime", "kronos_direction"],
    requireAll: true,
  },
  {
    panel: "TTM Squeeze",
    fieldGroup: "multi-timeframe squeeze",
    fields: ["squeeze_1h_status", "squeeze_4h_status", "squeeze_daily_status"],
  },
  {
    panel: "Volume Flow",
    fieldGroup: "CMF/MFI/OBV",
    fields: ["cmf_20", "mfi_14", "obv_divergence_10"],
  },
  {
    panel: "Order Flow",
    fieldGroup: "institutional/hidden/fake-wall",
    fields: ["institutional_direction", "institutional_score", "order_flow_status", "order_flow_age_sec"],
    requireAll: true,
  },
];

function dataForSymbol(intelMap: IntelMap, symbol: string): IntelData {
  return intelMap[symbol] || ({ symbol } as IntelData);
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

function qualitySeverity(present: number, expected: number): CommandCenterDataQualityRow["severity"] {
  if (present === expected) return "ok";
  if (present === 0) return "broken";
  return "watch";
}

export function buildCommandCenterDataQualityRows(symbols: readonly string[], intelMap: IntelMap): CommandCenterDataQualityRow[] {
  return CRITICAL_FIELD_GROUPS.map((group) => {
    const missingSymbols: string[] = [];
    let present = 0;

    for (const symbol of symbols) {
      const data = dataForSymbol(intelMap, symbol);
      const fieldValues = group.fields.map((field) => data[field]);
      const symbolPresent = group.requireAll ? fieldValues.every(hasValue) : fieldValues.some(hasValue);
      if (symbolPresent) {
        present += 1;
      } else {
        missingSymbols.push(symbol);
      }
    }

    return {
      panel: group.panel,
      fieldGroup: group.fieldGroup,
      present,
      expected: symbols.length,
      missingSymbols,
      severity: qualitySeverity(present, symbols.length),
    };
  });
}

function stateStatus(state: QueryState<unknown>): DataSourceStatus {
  if (state.status === "loading") return "loading";
  if (state.status === "error") return "error";
  return "live";
}

function recordCount(state: QueryState<Record<string, unknown>>): number | null {
  return state.status === "success" && state.data ? Object.keys(state.data).length : null;
}

function freshnessForState(state: QueryState<unknown>, successLabel: string): string {
  if (state.status === "loading") return "loading";
  if (state.status === "error") return state.error.message;
  return successLabel;
}

export function buildCommandCenterDataSourceRows(args: BuildCommandCenterDataSourcesArgs): CommandCenterDataSourceRow[] {
  const intelCount = recordCount(args.intelState);
  const volumeCount = recordCount(args.volumeFlowState);
  const orderFlowIsLiveWs = args.orderFlowTransportLabel === "LIVE WS";
  const orderFlowTransport = orderFlowIsLiveWs
    ? "LIVE WS"
    : args.orderFlowTransportLabel === "WS FALLBACK"
      ? "Convex snapshot (WS fallback)"
      : "Convex snapshot";

  return [
    {
      panel: "Instrument cards",
      source: "Convex getLatestIntelBySymbol + realtime WS overlay",
      transport: orderFlowIsLiveWs ? "Convex snapshot + LIVE WS" : orderFlowTransport,
      freshness: freshnessForState(args.intelState, intelCount === null ? "loaded" : `${intelCount}/5 symbols`),
      fallback: "Snapshot remains visible if realtime WS drops",
      decisionImpact: "Stale/missing price or snapshot blocks Command Center actionability",
      behavior: orderFlowIsLiveWs ? "LIVE" : "SNAPSHOT",
      status: stateStatus(args.intelState),
    },
    {
      panel: "Profitability Leak Watch",
      source: "Worker /api/admin/profitability-insights",
      transport: "Authenticated admin API",
      freshness: args.admin ? "admin token required" : "admin only",
      fallback: "No public diagnostic JSON fallback",
      decisionImpact: "Audit-only; never changes live gates without manual promotion",
      behavior: "READ-ONLY",
      status: args.admin ? "live" : "disabled",
    },
    {
      panel: "TTM Squeeze",
      source: "Convex getLatestIntelBySymbol squeeze fields",
      transport: "Convex subscription",
      freshness: freshnessForState(args.intelState, intelCount === null ? "loaded" : `${intelCount}/5 symbols`),
      fallback: "Pending states render explicitly when scans are not due",
      decisionImpact: "Displayed as context; does not create a trade by itself",
      behavior: "LOG-ONLY",
      status: stateStatus(args.intelState),
    },
    {
      panel: "Volume Flow",
      source: "Convex getLatestVolumeFlow + intel merge",
      transport: "Convex subscription",
      freshness: freshnessForState(args.volumeFlowState, volumeCount === null ? "loaded" : `${volumeCount}/5 symbols`),
      fallback: "Null fields render as neutral/blank, not fabricated",
      decisionImpact: "Stale volume flow blocks actionability, but cannot force a trade",
      behavior: "LOG-ONLY",
      status: stateStatus(args.volumeFlowState),
    },
    {
      panel: "Order Flow",
      source: orderFlowIsLiveWs ? "Realtime market-flow WS" : "Convex liveMarketSnapshots",
      transport: orderFlowTransport,
      freshness: orderFlowIsLiveWs ? "live push stream" : freshnessForState(args.intelState, "snapshot freshness checked per row"),
      fallback: "Falls back to Convex snapshot when WS is unavailable",
      decisionImpact: "Stale order flow blocks actionability and is labeled per symbol",
      behavior: orderFlowIsLiveWs ? "LIVE" : "SNAPSHOT",
      status: stateStatus(args.intelState),
    },
    {
      panel: "Level Intelligence",
      source: "Convex getLatestLevelBounceForecast",
      transport: "Convex subscription",
      freshness: freshnessForState(args.levelForecastState, "latest forecast snapshot"),
      fallback: "Shows missing/pending state; never creates levels client-side",
      decisionImpact: "Research context only until promotion gates pass",
      behavior: "LOG-ONLY",
      status: stateStatus(args.levelForecastState),
    },
    {
      panel: "Recent Results",
      source: "Convex getRecentSignals",
      transport: "Convex subscription",
      freshness: freshnessForState(args.signalsState, "last 30 signal rows"),
      fallback: "Empty state when no rows match selected session",
      decisionImpact: "Read-only trade history; no effect on current verdicts",
      behavior: "READ-ONLY",
      status: stateStatus(args.signalsState),
    },
    {
      panel: "Performance Strip",
      source: "Convex getStats",
      transport: "Convex subscription",
      freshness: freshnessForState(args.statsState, "aggregate stats loaded"),
      fallback: "Hidden until stats load; no guessed P&L",
      decisionImpact: "Read-only aggregate performance; no effect on current verdicts",
      behavior: "READ-ONLY",
      status: stateStatus(args.statsState),
    },
  ];
}

export function buildSqueezePanelSignals(symbols: readonly string[], intelMap: IntelMap): SqueezePanelSignal[] {
  return symbols.map((symbol) => {
    const data = dataForSymbol(intelMap, symbol);
    return {
      symbol,
      squeeze_pct: data.squeeze_pct ?? null,
      squeeze_tier: data.squeeze_tier ?? null,
      momentum_direction: data.squeeze_momentum_direction ?? null,
      action: data.squeeze_action ?? (data.squeeze_on ? "enter" : data.squeeze_fired ? "watch" : "monitor"),
      tf_1h: {
        on: data.squeeze_1h_on ?? null,
        fired: data.squeeze_1h_fired ?? null,
        direction: data.squeeze_1h_direction ?? null,
        stage: data.squeeze_1h_stage ?? null,
        pct: data.squeeze_1h_pct ?? null,
        pro_level: data.squeeze_1h_pro_level ?? null,
        status: data.squeeze_1h_status ?? null,
        status_reason: data.squeeze_1h_status_reason ?? null,
      },
      tf_4h: {
        on: data.squeeze_4h_on ?? null,
        fired: data.squeeze_4h_fired ?? null,
        direction: data.squeeze_4h_direction ?? null,
        stage: data.squeeze_4h_stage ?? null,
        pct: data.squeeze_4h_pct ?? null,
        pro_level: data.squeeze_4h_pro_level ?? null,
        status: data.squeeze_4h_status ?? null,
        status_reason: data.squeeze_4h_status_reason ?? null,
      },
      tf_daily: {
        on: data.squeeze_daily_on ?? null,
        fired: data.squeeze_daily_fired ?? null,
        direction: data.squeeze_daily_direction ?? null,
        stage: data.squeeze_daily_stage ?? null,
        pct: data.squeeze_daily_pct ?? null,
        pro_level: data.squeeze_daily_pro_level ?? null,
        status: data.squeeze_daily_status ?? null,
        status_reason: data.squeeze_daily_status_reason ?? null,
      },
    };
  });
}

export function buildVolumePanelSignals(symbols: readonly string[], intelMap: IntelMap): VolumePanelSignal[] {
  return symbols.map((symbol) => {
    const data = dataForSymbol(intelMap, symbol);
    return {
      symbol,
      obv_divergence_10: data.obv_divergence_10 ?? null,
      obv_divergence_20: data.obv_divergence_20 ?? null,
      cmf_20: data.cmf_20 ?? null,
      mfi_14: data.mfi_14 ?? null,
      fbd_flow_score: data.fbd_flow_score ?? null,
      mr_flow_score: data.mr_flow_score ?? null,
    };
  });
}

export function buildOrderFlowPanelSignals(symbols: readonly string[], intelMap: IntelMap): OrderFlowPanelSignal[] {
  return symbols.map((symbol) => {
    const data = dataForSymbol(intelMap, symbol);
    return {
      symbol,
      iceberg_bid: data.iceberg_bid ?? null,
      iceberg_ask: data.iceberg_ask ?? null,
      absorption_bid: data.absorption_bid ?? null,
      absorption_ask: data.absorption_ask ?? null,
      institutional_direction: data.institutional_direction ?? null,
      institutional_score: data.institutional_score ?? null,
      spoof_bid_risk: data.spoof_bid_risk ?? null,
      spoof_ask_risk: data.spoof_ask_risk ?? null,
      order_flow_status: data.order_flow_status ?? null,
      order_flow_age_sec: data.order_flow_age_sec ?? null,
      snapshot_created_at: data.created_at ?? null,
    };
  });
}
